import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { LessonFullDTO, ExamSummaryDTO } from '@amrita/shared';
import { EXAM_PASS_SCORE, QUIZ_PASS_SCORE, passScorePct } from '@amrita/shared';
import { TopBar } from '@/components/ui/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Markdown } from '@/components/ui/Markdown';
import { Bot, Play, CheckCircle2 } from 'lucide-react';
import { haptic, notifyOk } from '@/lib/tg';

export default function LessonPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data: lesson, isLoading } = useQuery<LessonFullDTO>({
    queryKey: ['lesson', id],
    queryFn: () => api.get<LessonFullDTO>(`/api/training/lessons/${id}`),
    enabled: !!id,
  });

  // mark as in_progress при первом открытии доступного урока.
  // Зависим именно от id+status, а не от всего объекта lesson: иначе любой
  // refetch (например после invalidate из toggleBookmark) давал бы новую ссылку
  // и слал лишний /start. Читаем поля в локальные переменные, чтобы
  // exhaustive-deps видел точные зависимости.
  const lessonId = lesson?.id;
  const lessonStatus = lesson?.status;
  useEffect(() => {
    if (lessonId && lessonStatus === 'available') {
      api.post<{ ok: true }>(`/api/training/lessons/${lessonId}/start`, {}).catch(() => {});
    }
  }, [lessonId, lessonStatus]);

  const complete = useMutation({
    mutationFn: () => api.post<{ ok: true }>(`/api/training/lessons/${lesson!.id}/complete`, {}),
    onSuccess: () => {
      notifyOk();
      qc.invalidateQueries({ queryKey: ['curriculum'] });
      qc.invalidateQueries({ queryKey: ['lesson', id] });
      nav('/training');
    },
  });

  const toggleBookmark = useMutation({
    mutationFn: async (articleId: string) => {
      const current = lesson?.articles.find((a) => a.id === articleId);
      if (!current) return;
      if (current.isBookmarked) {
        await api.delete<{ ok: true }>(`/api/training/bookmarks/${articleId}`);
      } else {
        await api.post<{ ok: true }>(`/api/training/bookmarks/${articleId}`, {});
      }
    },
    onSuccess: () => {
      haptic('light');
      qc.invalidateQueries({ queryKey: ['lesson', id] });
    },
  });

  // Pre-exam confirmation sheet (Day-18). Открывается по тапу «Начать экзамен»,
  // подгружает ExamSummaryDTO ленивым useQuery (enabled только когда sheet открыт),
  // показывает ставки до запуска квиза.
  const [examSheetOpen, setExamSheetOpen] = useState(false);
  const { data: examSummary, isLoading: examSummaryLoading } = useQuery<ExamSummaryDTO>({
    queryKey: ['exam-summary', id],
    queryFn: () => api.get<ExamSummaryDTO>(`/api/training/lessons/${id}/exam-summary`),
    enabled: !!id && examSheetOpen,
  });

  if (isLoading || !lesson) return <div className="p-6 text-tg-hint">Загрузка…</div>;

  const articlesCount = lesson.articles.length;
  const mod10 = articlesCount % 10;
  const mod100 = articlesCount % 100;
  const articlesPlural =
    mod10 === 1 && mod100 !== 11
      ? 'статья'
      : mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)
        ? 'статьи'
        : 'статей';

  return (
    <div className="safe-bottom min-h-full">
      <TopBar
        title={`Урок ${lesson.dayNumber}. ${lesson.title}`}
        subtitle={`~${lesson.estimatedMin ?? 30} мин · ${articlesCount} ${articlesPlural}`}
      />

      <div className="p-4 space-y-4">
        {lesson.description && <p className="text-tg-hint">{lesson.description}</p>}

        {lesson.articles.length === 0 && lesson.dayNumber !== 18 && (
          <Card className="text-center text-tg-hint">
            В этот урок пока не подгружены статьи. Попробуй позже или обратись к админу.
          </Card>
        )}

        {lesson.articles.map((a, idx) => (
          <Card key={a.id} className="space-y-3">
            <div className="flex justify-between items-start gap-2">
              <h2 className="text-lg font-semibold flex-1">
                {a.title}
                {lesson.articles.length > 1 && (
                  <span className="ml-2 align-middle text-xs font-normal text-tg-hint bg-tg-secondaryBg rounded-full px-2 py-0.5">
                    {idx + 1}/{lesson.articles.length}
                  </span>
                )}
              </h2>
              <button
                onClick={() => toggleBookmark.mutate(a.id)}
                disabled={toggleBookmark.isPending}
                className="text-brand-orange shrink-0 px-2 py-1 rounded hover:bg-tg-secondaryBg"
                aria-label={a.isBookmarked ? 'Убрать из закладок' : 'Добавить в закладки'}
              >
                {a.isBookmarked ? '🔖' : '📑'}
              </button>
            </div>
            <Markdown>{a.bodyMd}</Markdown>

            {a.mediaUrls.some((m) => m.type === 'image' && m.url) && (
              <div className="space-y-2 mt-2">
                {a.mediaUrls
                  .filter((m) => m.type === 'image' && m.url)
                  .map((m, i) => (
                    <img
                      key={i}
                      src={m.url}
                      alt={m.title ?? 'Изображение'}
                      className="w-full rounded-lg object-contain max-h-72"
                      loading="lazy"
                    />
                  ))}
              </div>
            )}

            {a.mediaUrls.some((m) => m.type === 'video') && (
              <div className="flex flex-wrap gap-2 mt-2">
                {a.mediaUrls
                  .filter((m) => m.type === 'video')
                  .map((m, i) => (
                    <a
                      key={i}
                      href={m.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-3 py-2 bg-tg-secondaryBg rounded-lg text-sm"
                    >
                      <Play className="w-4 h-4" />
                      {m.title ?? 'Видео'}
                    </a>
                  ))}
              </div>
            )}
          </Card>
        ))}

        {/* Day 18 → финальный экзамен (15 вопросов по всему курсу, проходной из shared) */}
        {lesson.dayNumber === 18 ? (
          examSheetOpen ? (
            <Card className="space-y-4 border-2 border-brand-orange/40 bg-brand-orange/5">
              <div className="flex items-center gap-2 font-semibold">
                <Bot className="w-5 h-5 text-brand-orange" />
                Перед началом экзамена
              </div>
              {examSummaryLoading || !examSummary ? (
                <p className="text-sm text-tg-hint">Загрузка…</p>
              ) : (
                <ul className="space-y-1.5 text-sm">
                  <li className="font-semibold">Попытка №{examSummary.attemptsUsed + 1}</li>
                  <li className="text-tg-hint">
                    {examSummary.totalQuestions} вопросов · ~20 мин
                  </li>
                  <li className="text-tg-hint">Проходной балл — {examSummary.passPct}%</li>
                  {examSummary.prevBestScore != null && (
                    <li className="text-tg-hint">
                      Лучший результат: {examSummary.prevBestScore.toFixed(1)}/10
                    </li>
                  )}
                </ul>
              )}
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                disabled={examSummaryLoading}
                onClick={() => nav(`/training/lesson/${lesson.id}/quiz?exam=1`)}
              >
                Начать экзамен
              </Button>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => setExamSheetOpen(false)}
              >
                Отмена
              </Button>
            </Card>
          ) : (
            <Card className="space-y-3 border-2 border-brand-orange/40 bg-brand-orange/5">
              <div className="flex items-center gap-2 font-semibold">
                <Bot className="w-5 h-5 text-brand-orange" />
                Финальный экзамен
              </div>
              <p className="text-sm text-tg-hint">
                15 вопросов по всем 18 дням курса (включая ролевые ситуации с клиентами).
                Проходной балл — {passScorePct(EXAM_PASS_SCORE)}%. Каждая попытка — новый случайный набор вопросов по всему курсу.
              </p>
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => {
                  haptic('light');
                  setExamSheetOpen(true);
                }}
              >
                Начать экзамен
              </Button>
            </Card>
          )
        ) : (
          lesson.quizQuestionsCount > 0 && (
            <Card className="space-y-3">
              <div className="flex items-center gap-2 font-semibold">
                <Bot className="w-5 h-5 text-brand-orange" />
                AI-тест
              </div>
              <p className="text-sm text-tg-hint">
                {lesson.quizQuestionsCount} вопросов, проверка через Claude Sonnet. Проходной балл {passScorePct(QUIZ_PASS_SCORE)}%.
              </p>
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => nav(`/training/lesson/${lesson.id}/quiz`)}
              >
                Начать тест
              </Button>
            </Card>
          )
        )}

        {lesson.status !== 'completed' && lesson.quizQuestionsCount === 0 && (
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            disabled={complete.isPending}
            onClick={() => complete.mutate()}
          >
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Отметить урок пройденным
          </Button>
        )}

        <div className="flex gap-2">
          <Button
            variant="secondary"
            className="flex-1"
            onClick={() =>
              nav(`/ai?ctx=${encodeURIComponent('Урок ' + lesson.dayNumber + ': ' + lesson.title)}`)
            }
          >
            <Bot className="w-4 h-4 mr-2" />
            Спросить ИИ
          </Button>
        </div>
      </div>
    </div>
  );
}
